import { Component, ViewChild } from '@angular/core';
import { Store, createSelector } from '@ngrx/store';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';

import { ColorSchemeService } from 'src/app/color-scheme.service';
import { dayjs } from 'src/app/core/dayjs/dayjs';
import { FreyaCommonModule } from 'src/app/freya-common/freya-common.module';
import { jobCreateCustomerFeature } from 'src/app/job/job-create/job-create-customer/job-create-customer.feature';
import { InventoryTableComponent } from 'src/app/job/job-inventory/inventory-table/inventory-table.component';
import { overviewTimelineFeature } from 'src/app/job/job-overview/overview-timeline/overview-timeline.feature';
import { TimelineBookEventComponent } from 'src/app/job/job-overview/overview-timeline/timeline-book-event/timeline-book-event.component';
import { JobToolActions } from 'src/app/job/job-tool.actions';
import { selectCalculatorUrl, selectCustomerEditable, selectJob, selectJobEditDisabledWarning, selectJobId, selectJobZone } from 'src/app/job/job-tool.reducer';
import { EventsTableComponent } from 'src/app/job/job-workorders/events-table/events-table.component';
import { MrCalculatorComponent } from 'src/app/job/job-workorders/mr-calculator/mr-calculator.component';
import { selectScriptLoaded, selectElements } from 'src/app/quickjs/quickjs.feature';
import { JobHeaderDetailsComponent } from 'src/app/shared/job-header-details/job-header-details.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { UnsavedChangesBannerComponent } from 'src/app/shared/unsaved-changes-banner/unsaved-changes-banner.component';
import { selectChanges } from 'src/app/state/unsaved-changes.selectors';
import { LoadingState } from 'src/app/utilities/state.util';

import { NewOpportunityAddInventoryComponent } from './new-opportunity-add-inventory/new-opportunity-add-inventory.component';
import { NewOpportunityCustomerActions } from './new-opportunity-customer/new-opportunity-customer.actions';
import { NewOpportunityCustomerComponent } from './new-opportunity-customer/new-opportunity-customer.component';
import { NewOpportunityLocationsComponent } from './new-opportunity-locations/new-opportunity-locations.component';
import { NewOpportunitySearchCustomersDialogActions } from './new-opportunity-search-customers-dialog/new-opportunity-search-customers-dialog.actions';
import { NewOpportunitySummaryComponent } from './new-opportunity-summary/new-opportunity-summary.component';
import { NewOpportunityActions } from './new-opportunity.actions';
import { newOpportunityFeature } from './new-opportunity.feature';
import { NEW_OPPORTUNITY_STEP_TITLES } from './new-opportunity.state';
import { StepNavigationComponent } from './step-navigation/step-navigation.component';
import { StepperComponent } from './stepper/stepper.component';

@Component({
    selector: 'app-new-opportunity',
    standalone: true,
    imports: [
        FreyaCommonModule,
        SharedModule,
        EventsTableComponent,
        InventoryTableComponent,
        JobHeaderDetailsComponent,
        MrCalculatorComponent,
        NewOpportunityCustomerComponent,
        NewOpportunityLocationsComponent,
        NewOpportunitySummaryComponent,
        StepNavigationComponent,
        StepperComponent,
        UnsavedChangesBannerComponent,
        NewOpportunityAddInventoryComponent,
        TimelineBookEventComponent,
        ],
    templateUrl: './new-opportunity.component.html',
    styleUrls: [
      './new-opportunity.component.scss',
      '../job/colors.scss',
    ]
})
export class NewOpportunityComponent {

    @ViewChild('actionsMenu') actionsMenu!: Menu;

    actions = NewOpportunityActions;
    customerActions = NewOpportunityCustomerActions;
    searchCustomersDialogActions = NewOpportunitySearchCustomersDialogActions;
    jobToolActions = JobToolActions;

    viewModel$ = this.store.select(createSelector({
        step: newOpportunityFeature.selectStep,
        title: createSelector(newOpportunityFeature.selectStep, (step) => NEW_OPPORTUNITY_STEP_TITLES[step]),
        hasJob: createSelector(selectJob, (job) => Boolean(job)),
        editDisabledWarning: selectJobEditDisabledWarning,
        jobMenuItems: createSelector(
            selectChanges,
            selectJobId,
            selectJobZone,
            overviewTimelineFeature.selectTimeline,
            (changes, jobId, zone, timeline) => {
                if (!jobId) { return []; }

                const saving = changes.some((c) => c.jobId === jobId && c.saving);
                const zoneId = zone?.type === 'area' ? zone.parent?.id : zone?.id;
                const q = zoneId ? { zone: zoneId } : {};

                const scheduleDate = timeline?.length
                    ? dayjs(timeline[0]).format('YYYY-MM-DD')
                    : dayjs().format('YYYY-MM-DD');

                const items: MenuItem[] = [
                    {
                        label: saving ? 'Saving…' : 'Go to Job',
                        icon: saving ? 'pi pi-spin pi-spinner' : 'pi pi-briefcase',
                        disabled: saving,
                        command: () => this.store.dispatch(NewOpportunityActions.goToJobButtonClicked()),
                    },
                    {
                        label: 'Overview',
                        icon: 'pi pi-home',
                        routerLink: [`/jobs/${jobId}/overview`],
                        queryParams: q,
                    },
                    {
                        label: 'Open Schedule',
                        icon: 'pi pi-calendar',
                        routerLink: [ '/schedule/day' ],
                        queryParams: { date: scheduleDate },
                    },
                    {
                        label: 'Workorders',
                        icon: 'pi pi-list',
                        routerLink: [`/jobs/${jobId}/workorders`],
                        queryParams: q,
                    },
                    {
                        label: 'Billing',
                        icon: 'pi pi-dollar',
                        routerLink: [`/jobs/${jobId}/billing`],
                        queryParams: q,
                    },
                ];

                return items;
            },
        ),
        showSkeleton: createSelector(
            selectJob,
            newOpportunityFeature.selectCreateJobCall,
            (job, createJobCall) => createJobCall === LoadingState.MUTATING || (createJobCall === LoadingState.MUTATED && !job),
        ),
        showCalculator: createSelector(
            selectCalculatorUrl,
            selectElements,
            selectScriptLoaded,
            (calculatorUrl, elements, loaded) => Boolean(
                calculatorUrl
                && (!loaded || elements?.length)
            )
        ),
        showCustomerActionButtons: createSelector(
            newOpportunityFeature.selectStep,
            (step) => step === 'customer',
        ),
        selectedCustomer: createSelector(
            jobCreateCustomerFeature.selectCustomerEditor,
            (editor) => editor.selectedCustomer,
        ),
        isCustomerModified: createSelector(
            jobCreateCustomerFeature.selectCustomerEditor,
            (editor) => editor.isCustomerModified,
        ),
        customerEditable: selectCustomerEditable,
    }));

    constructor(
        public store: Store,
        public colorScheme: ColorSchemeService,
    ) {}

    toggleActionsMenu(event: Event) {
        this.actionsMenu.toggle(event);
    }
}
